<template>
  <div class="user-widget">
    <div v-if="isAuthenticated && user" class="user-info">
      <div class="user-dropdown" @click="toggleDropdown">
        <div
            class="user-avatar"
            :class="{ 'user-avatar--fallback': !imageLoaded }"
            :style="user.picture ? { backgroundImage: `url(${user.picture})` } : undefined"
        >
          <span v-if="!imageLoaded" class="user-avatar-initials">{{ userInitials }}</span>
        </div>
        <span class="user-name">{{ user.name }}</span>
        <svg class="dropdown-arrow" :class="{ 'open': showDropdown }" width="12" height="12" viewBox="0 0 12 12"
             fill="none">
          <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>

      <div v-if="showDropdown" class="dropdown-menu">
        <div class="dropdown-item user-email utility-button-font">
          {{ user.name }}
        </div>
        <div class="dropdown-divider"></div>
        <button class="dropdown-item utility-button-font" @click="goToProfile">
          {{ $t('header.user.mySubscription') }}
        </button>
        <button class="dropdown-item utility-button-font" @click="goToMySettings">
          {{ $t('header.user.mySettings') }}
        </button>
        <button class="dropdown-item utility-button-font" @click="goToFeedback">
          {{ $t('header.user.sendFeedback') }}
        </button>
        <button v-if="!isTelegramUser" class="dropdown-item logout-btn utility-button-font" @click="handleLogout">
          <div class="dropdown-divider"></div>
          {{ $t('header.user.signOut') }}
        </button>
      </div>
    </div>

    <div v-else class="unauthorized-buttons">
      <button class="login-button utility-button-font" @click="handleLogin">
        {{ $t('header.user.signIn') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Language, TranslationMode } from '@lingvo-news/contracts';
import { getCurrentUserStore } from '../../../store/services';
import { parseCommonRouteParamsFromParams } from '../../../composables/useCommonRouteParams';
import { isTelegramMiniApp } from '../../../infrastructure/auth/isTelegramMiniApp';

const route = useRoute();
const router = useRouter();
const userStore = getCurrentUserStore();
const showDropdown = ref(false);
const imageLoaded = ref(false);

const isAuthenticated = computed(() => userStore.getCurrentUserId() != null);
const user = computed(() => userStore.getCurrentUser());
const isTelegramUser = computed(() => isTelegramMiniApp());

watch(() => user.value?.picture, (url) => {
  imageLoaded.value = false;
  if (!url) return;
  const img = new Image();
  img.onload = () => { imageLoaded.value = true; };
  img.onerror = () => { imageLoaded.value = false; };
  img.src = url;
  if (img.complete) {
    imageLoaded.value = true;
  }
}, { immediate: true });

const userInitials = computed(() => {
  if (!user.value?.name) return '?';
  const names = user.value.name.split(' ');
  if (names.length >= 2) {
    return (names[0][0] + names[1][0]).toUpperCase();
  }
  return user.value.name.substring(0, 2).toUpperCase();
});

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const closeDropdown = () => {
  showDropdown.value = false;
};

const goToMySettings = async () => {
  await router.push({
    name: 'my-settings',
  });
  closeDropdown();
};

const goToFeedback = async () => {
  await router.push({
    name: 'feedback',
  });
  closeDropdown();
};

const goToProfile = async () => {
  await router.push({
    name: 'my-subscription',
  });
  closeDropdown();
};

const handleLogin = () => {
  router.push({ path: '/login' });
};

const handleLogout = async () => {
  await userStore.logout();
  closeDropdown();
  // Redirect to home after logout
  router.push({ path: '/' });
};

const goToIntro = async () => {
  await router.push({
    name: 'intro',
  });
};

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.user-widget')) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.user-widget {
  position: relative;
}

.login-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.login-button:active {
  transform: translateY(0);
}

.user-info {
  position: relative;
}

.user-dropdown {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.user-dropdown:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: transparent;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: 2px solid rgba(255, 255, 255, 0.3);
  outline-offset: -1px;
  overflow: hidden;
  flex-shrink: 0;
}

.user-avatar--fallback {
  background-color: #764ba2;
}

.user-avatar-initials {
  color: white;
  font-size: 14px;
  line-height: 1;
  pointer-events: none;
}

.user-name {
  color: white;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.dropdown-arrow {
  color: white;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  overflow: hidden;
  z-index: 1000;
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 12px 16px;
  text-align: left;
  color: #333;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--font-size-button);
  transition: background 0.2s ease;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item.user-email {
  color: #666;
  cursor: default;
  padding: 10px 16px;
}

.dropdown-item.user-email:hover {
  background: transparent;
}

.dropdown-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 4px 0;
}

.logout-btn {
  color: #dc3545;
}

.logout-btn:hover {
  background: #fff5f5;
}

.unauthorized-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.settings-button {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.settings-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

@media (max-width: 800px) {
  .user-name {
    display: none;
  }

  .user-dropdown {
    min-width: auto;
    padding: 6px;
  }

  .login-button,
  .settings-button {
    padding: 8px 16px;
    font-size: 13px;
    white-space: nowrap;
  }
}

@media (max-width: 576px) {
  .dropdown-menu {
    left: auto;
    right: 0;
    min-width: 200px;
    max-width: calc(100vw - 16px);
  }
}
</style>
